/**
 * Teenused ja paketid - the shell over the two halves of the plan catalogue.
 *
 * Both halves used to be the shared `@wa/ui` managers rendered as-is. Those draw
 * web_admin's language - bordered cards, square inputs, a dashed grey empty box -
 * so this page arrived as a slab of another product in the middle of a CRM built
 * out of pills and sunken fields. The DATA is still shared (the headless hooks in
 * `@wa/ui`, so web_admin and the CRM cannot drift apart on what a service is);
 * only the chrome is the CRM's own now.
 *
 * The tab strip is `PillTabs` rather than the framer-motion `layoutId` pill it
 * replaced. The inverted pill is already how the sidebar says "you are here", and
 * a second animated highlight on the same screen is a second thing to learn for
 * the same fact.
 *
 * `PopHost` wraps the whole page on purpose: every row menu underneath reads its
 * open slot from this context, and with no host the three-dot buttons still
 * depress but no menu ever opens - silently, with nothing in the console.
 */
import { useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import { PopHost } from "../../shell/Pop";
import { PageHead, PillTabs, Toolbar } from "../../ui";
import { PlansPanel } from "./plans";
import { ServicesPanel } from "./services/ServicesPanel";

type TabKey = "services" | "packages";

const TABS: TabKey[] = ["services", "packages"];

export function PlansSection(_props: { user: StaffUser }) {
  const t = useT();
  const [tab, setTab] = useState<TabKey>("services");

  return (
    <PopHost>
      <PageHead title={t("nav.plans")} sub={t("plans.sub")} />

      <Toolbar>
        <PillTabs
          value={tab}
          onChange={setTab}
          tabs={TABS.map((key) => ({ value: key, label: t(`nav.${key}`) }))}
        />
      </Toolbar>

      {/* Each half brings its own create action, because only it knows what it
          is creating - the shell would have to switch on the tab to render the
          right button, which is the same decision made twice. */}
      {tab === "services" ? <ServicesPanel /> : <PlansPanel />}
    </PopHost>
  );
}
